import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { DeleteIcon, RepeatIcon } from "@chakra-ui/icons";
import { useLocation } from "react-router-dom";
import payrollService, {
  formatPayrollCurrency,
} from "../modules/payroll/payrollService";
import { employeeService } from "../modules/employee/employeeService";
import RunsSection from "../modules/payroll_compliance/sections/RunsSection";
import CtcSection from "../modules/payroll_compliance/sections/CtcSection";
import ComplianceSection from "../modules/payroll_compliance/sections/ComplianceSection";
import BenefitsSection from "../modules/payroll_compliance/sections/BenefitsSection";
import FnfSection from "../modules/payroll_compliance/sections/FnfSection";
import Form16Section from "../modules/payroll_compliance/sections/Form16Section";
import BankSection from "../modules/payroll_compliance/sections/BankSection";
import PayslipsSection from "../modules/payroll_compliance/sections/PayslipsSection";
import {
  CardHeadingWithHelp,
  SECTION_HELP_TEXT,
  CARD_HELP_TEXT,
  ManualHint,
} from "../modules/payroll_compliance/shared/help";
import {
  DEFAULT_META,
  DEFAULT_COMPONENTS,
  BLANK_COMPONENT,
  DEFAULT_PT_SLABS_JSON,
  SECTION_CONFIG,
  getUserFromStorage,
  getSectionKey,
  resetStructureForm,
  resetCompensationForm,
  resetRunForm,
  resetBenefitForm,
  resetAdjustmentForm,
  resetFnfForm,
  resetForm16Form,
} from "../modules/payroll_compliance/shared/constants";

/**
 * Renders an editable table of salary components with add / remove row controls.
 * Receives `components` (array) and `onChange(newArray)` as props.
 */
const SalaryComponentsEditor = ({ components, onChange }) => {
  const updateRow = (idx, field, value) => {
    const updated = components.map((c, i) =>
      i === idx ? { ...c, [field]: value } : c,
    );
    onChange(updated);
  };

  const addRow = () => onChange([...components, { ...BLANK_COMPONENT }]);

  const removeRow = (idx) => onChange(components.filter((_, i) => i !== idx));

  return (
    <Stack spacing={3}>
      {components.map((comp, idx) => (
        <Box
          key={idx}
          p={3}
          borderWidth={1}
          borderRadius="md"
          bg="gray.50"
          _dark={{ bg: "gray.700" }}
        >
          <HStack spacing={2} flexWrap="wrap" alignItems="flex-start">
            {/* Key */}
            <FormControl minW="110px" flex="1">
              <FormLabel fontSize="xs" mb={1}>
                Key
              </FormLabel>
              <Input
                size="sm"
                placeholder="e.g. BASIC"
                value={comp.key}
                onChange={(e) =>
                  updateRow(idx, "key", e.target.value.toUpperCase())
                }
              />
            </FormControl>

            {/* Type */}
            <FormControl minW="110px" flex="1">
              <FormLabel fontSize="xs" mb={1}>
                Type
              </FormLabel>
              <Select
                size="sm"
                value={comp.type}
                onChange={(e) => updateRow(idx, "type", e.target.value)}
              >
                <option value="EARNING">Earning</option>
                <option value="DEDUCTION">Deduction</option>
              </Select>
            </FormControl>

            {/* Calculation type */}
            <FormControl minW="150px" flex="1">
              <FormLabel fontSize="xs" mb={1}>
                Calculation
              </FormLabel>
              <Select
                size="sm"
                value={comp.calculationType}
                onChange={(e) =>
                  updateRow(idx, "calculationType", e.target.value)
                }
              >
                <option value="FIXED">Fixed</option>
                <option value="PERCENT_OF_BASIC">% of Basic</option>
                <option value="PERCENT_OF_CTC">% of CTC</option>
                <option value="FORMULA">Formula</option>
              </Select>
            </FormControl>

            {/* Value (hidden for FORMULA) */}
            {comp.calculationType !== "FORMULA" && (
              <FormControl minW="90px" flex="1">
                <FormLabel fontSize="xs" mb={1}>
                  Value
                </FormLabel>
                <Input
                  size="sm"
                  type="number"
                  min={0}
                  value={comp.value}
                  onChange={(e) =>
                    updateRow(idx, "value", Number(e.target.value))
                  }
                />
              </FormControl>
            )}

            {/* Formula (only for FORMULA) */}
            {comp.calculationType === "FORMULA" && (
              <FormControl minW="150px" flex="2">
                <FormLabel fontSize="xs" mb={1}>
                  Formula
                </FormLabel>
                <Input
                  size="sm"
                  placeholder="e.g. BASIC * 0.12"
                  value={comp.formula || ""}
                  onChange={(e) => updateRow(idx, "formula", e.target.value)}
                />
              </FormControl>
            )}

            {/* Checkboxes */}
            <VStack spacing={1} alignSelf="flex-end" pb={1} minW="85px">
              <Checkbox
                size="sm"
                isChecked={comp.isTaxable}
                onChange={(e) => updateRow(idx, "isTaxable", e.target.checked)}
              >
                Taxable
              </Checkbox>
              <Checkbox
                size="sm"
                isChecked={comp.isPFApplicable}
                onChange={(e) =>
                  updateRow(idx, "isPFApplicable", e.target.checked)
                }
              >
                PF
              </Checkbox>
              <Checkbox
                size="sm"
                isChecked={comp.isESIApplicable}
                onChange={(e) =>
                  updateRow(idx, "isESIApplicable", e.target.checked)
                }
              >
                ESI
              </Checkbox>
            </VStack>

            {/* Delete row */}
            <IconButton
              aria-label="Remove component"
              icon={<DeleteIcon />}
              size="sm"
              colorScheme="red"
              variant="ghost"
              onClick={() => removeRow(idx)}
              alignSelf="flex-end"
            />
          </HStack>
        </Box>
      ))}

      <Button
        size="sm"
        variant="outline"
        onClick={addRow}
        alignSelf="flex-start"
      >
        + Add Component
      </Button>
    </Stack>
  );
};

const PayrollPage = () => {
  const location = useLocation();
  const toast = useToast();
  const user = getUserFromStorage();
  const sectionKey = getSectionKey(location.pathname);
  const section = SECTION_CONFIG[sectionKey];

  const canManagePayroll = [
    "SuperAdmin",
    "Admin",
    "HRHead",
    "HRExecutive",
  ].includes(user?.role);
  const canFinance = ["SuperAdmin", "Admin", "Finance"].includes(user?.role);

  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(DEFAULT_META);
  const [employees, setEmployees] = useState([]);
  const [salaryStructures, setSalaryStructures] = useState([]);
  const [compensations, setCompensations] = useState([]);
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [statutoryConfig, setStatutoryConfig] = useState(null);
  const [complianceDashboard, setComplianceDashboard] = useState(null);
  const [benefits, setBenefits] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [fnfRecords, setFnfRecords] = useState([]);
  const [form16Records, setForm16Records] = useState([]);
  const [bankTransfers, setBankTransfers] = useState([]);
  const [bankPreview, setBankPreview] = useState(null);

  const [structureForm, setStructureForm] = useState(resetStructureForm());
  const [compensationForm, setCompensationForm] = useState(
    resetCompensationForm(),
  );
  const [runForm, setRunForm] = useState(resetRunForm());
  const [statutoryForm, setStatutoryForm] = useState({
    pfEmployeeContribution: "12",
    pfEmployerContribution: "12",
    pfWageLimit: "15000",
    esiEmployeeContribution: "0.75",
    esiEmployerContribution: "3.25",
    esiWageLimit: "21000",
    ptState: "KA",
    ptSlabsJson: DEFAULT_PT_SLABS_JSON,
    tdsRegime: "NEW",
  });
  const [benefitForm, setBenefitForm] = useState(resetBenefitForm());
  const [adjustmentForm, setAdjustmentForm] = useState(resetAdjustmentForm());
  const [fnfForm, setFnfForm] = useState(resetFnfForm());
  const [form16Form, setForm16Form] = useState(resetForm16Form());
  const [complianceEdits, setComplianceEdits] = useState({});
  const [bankEdits, setBankEdits] = useState({});
  const [form16Edits, setForm16Edits] = useState({});
  const [fnfEdits, setFnfEdits] = useState({});

  // Compliance details modal
  const [complianceModalRecord, setComplianceModalRecord] = useState(null);
  const {
    isOpen: isComplianceModalOpen,
    onOpen: openComplianceModal,
    onClose: closeComplianceModal,
  } = useDisclosure();

  const applyMeta = (result) => {
    if (result?.meta) {
      setMeta(result.meta);
    }
    return result?.data;
  };

  const showError = (title, error) => {
    toast({
      title,
      description: error?.response?.data?.message || error.message,
      status: "error",
      duration: 4000,
      isClosable: true,
    });
  };

  const fetchEmployees = async () => {
    if (!["ctc", "benefits", "fnf", "form16"].includes(sectionKey)) {
      return;
    }

    if (
      !["SuperAdmin", "Admin", "HRHead", "HRExecutive", "Finance"].includes(
        user?.role,
      )
    ) {
      return;
    }

    const data = await employeeService.getAllEmployees();
    setEmployees(Array.isArray(data) ? data : []);
  };

  const loadSectionData = async () => {
    setLoading(true);
    try {
      const metaResult = await payrollService.getMeta();
      applyMeta(metaResult);
      await fetchEmployees();

      if (sectionKey === "runs") {
        const [runsResult, payslipResult] = await Promise.all([
          payrollService.listRuns(),
          payrollService.listPayslips(),
        ]);
        setPayrollRuns(applyMeta(runsResult) || []);
        setPayslips(applyMeta(payslipResult) || []);
      }

      if (sectionKey === "ctc") {
        const [structureResult, compensationResult] = await Promise.all([
          payrollService.listSalaryStructures(),
          payrollService.listCompensations(),
        ]);
        setSalaryStructures(applyMeta(structureResult) || []);
        setCompensations(applyMeta(compensationResult) || []);
      }

      if (sectionKey === "compliance") {
        const [configResult, dashboardResult] = await Promise.all([
          payrollService.getStatutoryConfig(),
          payrollService.getComplianceDashboard(),
        ]);
        const nextConfig = applyMeta(configResult);
        setStatutoryConfig(nextConfig);
        setComplianceDashboard(applyMeta(dashboardResult));
        if (nextConfig) {
          setStatutoryForm({
            pfEmployeeContribution: String(
              nextConfig?.pf?.employeeContribution ?? 12,
            ),
            pfEmployerContribution: String(
              nextConfig?.pf?.employerContribution ?? 12,
            ),
            pfWageLimit: String((nextConfig?.pf?.wageLimit || 0) / 100),
            esiEmployeeContribution: String(
              nextConfig?.esi?.employeeContribution ?? 0.75,
            ),
            esiEmployerContribution: String(
              nextConfig?.esi?.employerContribution ?? 3.25,
            ),
            esiWageLimit: String((nextConfig?.esi?.wageLimit || 0) / 100),
            ptState: nextConfig?.pt?.state || "KA",
            ptSlabsJson: JSON.stringify(
              (nextConfig?.pt?.slabs || []).map((slab) => ({
                ...slab,
                min: (slab.min || 0) / 100,
                max:
                  slab.max === null || slab.max === undefined
                    ? null
                    : slab.max / 100,
                tax: (slab.tax || 0) / 100,
              })),
              null,
              2,
            ),
            tdsRegime: nextConfig?.tds?.regime || "NEW",
          });
        }
      }

      if (sectionKey === "benefits") {
        const [benefitResult, adjustmentResult] = await Promise.all([
          payrollService.listBenefits(),
          payrollService.listAdjustments(),
        ]);
        setBenefits(applyMeta(benefitResult) || []);
        setAdjustments(applyMeta(adjustmentResult) || []);
      }

      if (sectionKey === "fnf") {
        const fnfResult = await payrollService.listFnF();
        setFnfRecords(applyMeta(fnfResult) || []);
      }

      if (sectionKey === "form16") {
        const result = canFinance
          ? await payrollService.listForm16()
          : await payrollService.getOwnForm16();
        setForm16Records(applyMeta(result) || []);
      }

      if (sectionKey === "bank") {
        const result = await payrollService.listBankTransfers();
        setBankTransfers(applyMeta(result) || []);
      }

      if (sectionKey === "payslips") {
        const result = await payrollService.listPayslips();
        setPayslips(applyMeta(result) || []);
      }
    } catch (error) {
      showError("Failed to load payroll data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSectionData();
  }, [sectionKey]);

  const handleCreateRun = async () => {
    try {
      await payrollService.createRun({
        month: Number(runForm.month),
        year: Number(runForm.year),
      });
      toast({ title: "Payroll run created", status: "success" });
      setRunForm(resetRunForm());
      loadSectionData();
    } catch (error) {
      showError("Failed to create payroll run", error);
    }
  };

  const handleCreateStructure = async () => {
    try {
      await payrollService.createSalaryStructure({
        name: structureForm.name,
        code: structureForm.code,
        ctc: Number(structureForm.ctc),
        effectiveFrom: structureForm.effectiveFrom,
        components: structureForm.components,
      });
      toast({ title: "Salary structure created", status: "success" });
      setStructureForm(resetStructureForm());
      loadSectionData();
    } catch (error) {
      showError("Failed to save salary structure", error);
    }
  };

  const handleAssignCompensation = async () => {
    try {
      await payrollService.assignCompensation({
        userId: compensationForm.userId,
        salaryStructureId: compensationForm.salaryStructureId,
        baseSalary: Number(compensationForm.baseSalary),
        effectiveFrom: compensationForm.effectiveFrom,
        variables: JSON.parse(compensationForm.variablesJson || "{}"),
      });
      toast({ title: "Compensation assigned", status: "success" });
      setCompensationForm(resetCompensationForm());
      loadSectionData();
    } catch (error) {
      showError("Failed to assign compensation", error);
    }
  };

  const handleSaveStatutory = async () => {
    try {
      await payrollService.saveStatutoryConfig({
        pf: {
          enabled: true,
          employeeContribution: Number(statutoryForm.pfEmployeeContribution),
          employerContribution: Number(statutoryForm.pfEmployerContribution),
          wageLimit: Number(statutoryForm.pfWageLimit),
        },
        esi: {
          enabled: true,
          employeeContribution: Number(statutoryForm.esiEmployeeContribution),
          employerContribution: Number(statutoryForm.esiEmployerContribution),
          wageLimit: Number(statutoryForm.esiWageLimit),
        },
        pt: {
          state: statutoryForm.ptState,
          slabs: JSON.parse(statutoryForm.ptSlabsJson || "[]"),
        },
        tds: { regime: statutoryForm.tdsRegime },
      });
      toast({ title: "Statutory config updated", status: "success" });
      loadSectionData();
    } catch (error) {
      showError("Failed to save statutory config", error);
    }
  };

  const handleCreateBenefit = async () => {
    try {
      await payrollService.createBenefit({
        ...benefitForm,
        amount: Number(benefitForm.amount),
        month: Number(benefitForm.month),
        year: Number(benefitForm.year),
      });
      toast({ title: "Benefit saved", status: "success" });
      setBenefitForm(resetBenefitForm());
      loadSectionData();
    } catch (error) {
      showError("Failed to save benefit", error);
    }
  };

  const handleCreateAdjustment = async () => {
    try {
      await payrollService.createAdjustment({
        ...adjustmentForm,
        amount: Number(adjustmentForm.amount),
        effectiveMonth: Number(adjustmentForm.effectiveMonth),
        effectiveYear: Number(adjustmentForm.effectiveYear),
      });
      toast({ title: "Adjustment saved", status: "success" });
      setAdjustmentForm(resetAdjustmentForm());
      loadSectionData();
    } catch (error) {
      showError("Failed to save adjustment", error);
    }
  };

  const handleRunAction = async (action, runId) => {
    try {
      if (action === "process") {
        await payrollService.processRun(runId);
      } else {
        await payrollService.finalizeRun(runId);
      }
      toast({ title: `Payroll run ${action}ed`, status: "success" });
      loadSectionData();
    } catch (error) {
      showError(`Failed to ${action} payroll run`, error);
    }
  };

  const handleComplianceUpdate = async (recordId) => {
    try {
      await payrollService.updateComplianceRecord(
        recordId,
        complianceEdits[recordId] || {},
      );
      toast({ title: "Compliance record updated", status: "success" });
      loadSectionData();
    } catch (error) {
      showError("Failed to update compliance record", error);
    }
  };

  const handleBankTransferUpdate = async (transferId) => {
    try {
      await payrollService.updateBankTransfer(
        transferId,
        bankEdits[transferId] || {},
      );
      toast({ title: "Bank transfer updated", status: "success" });
      loadSectionData();
    } catch (error) {
      showError("Failed to update bank transfer", error);
    }
  };

  const handleBankPreview = async (payrollRunId) => {
    try {
      const result = await payrollService.getBankTransferPreview(payrollRunId);
      setBankPreview(applyMeta(result));
    } catch (error) {
      showError("Failed to load bank preview", error);
    }
  };

  const handleGenerateForm16 = async () => {
    try {
      await payrollService.generateForm16({
        ...form16Form,
        userId: form16Form.userId || undefined,
      });
      toast({ title: "Form 16 generated", status: "success" });
      setForm16Form(resetForm16Form());
      loadSectionData();
    } catch (error) {
      showError("Failed to generate Form 16", error);
    }
  };

  const handleUpdateForm16 = async (recordId) => {
    try {
      await payrollService.updateForm16(recordId, form16Edits[recordId] || {});
      toast({ title: "Form 16 updated", status: "success" });
      loadSectionData();
    } catch (error) {
      showError("Failed to update Form 16", error);
    }
  };

  const handleCreateFnf = async () => {
    try {
      await payrollService.createFnF({
        ...fnfForm,
        unpaidSalary: Number(fnfForm.unpaidSalary || 0),
        leaveEncashment: Number(fnfForm.leaveEncashment || 0),
        gratuity: Number(fnfForm.gratuity || 0),
        deductions: Number(fnfForm.deductions || 0),
      });
      toast({ title: "F&F record created", status: "success" });
      setFnfForm(resetFnfForm());
      loadSectionData();
    } catch (error) {
      showError("Failed to create F&F", error);
    }
  };

  const handleUpdateFnf = async (recordId) => {
    try {
      await payrollService.updateFnF(recordId, fnfEdits[recordId] || {});
      toast({ title: "F&F updated", status: "success" });
      loadSectionData();
    } catch (error) {
      showError("Failed to update F&F", error);
    }
  };

  return (
    <Box p={{ base: 4, md: 6 }}>
      <Stack spacing={6}>
        <HStack justify="space-between" align="flex-start" flexWrap="wrap">
          <Box>
            <HStack spacing={2} align="center">
              <Heading size="lg">{section.title}</Heading>
              <ManualHint
                text={
                  SECTION_HELP_TEXT[sectionKey] ||
                  "Quick help for this payroll section."
                }
              />
            </HStack>
            <Text color="gray.600" mt={2} maxW="3xl">
              {section.description}
            </Text>
          </Box>
          <Button leftIcon={<RepeatIcon />} onClick={loadSectionData}>
            Refresh
          </Button>
        </HStack>

        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            Payroll API contract: monetary values are stored and returned in
            paise. This UI converts them to rupees for display.
          </AlertDescription>
        </Alert>

        {loading ? (
          <VStack py={12}>
            <Spinner size="xl" color="blue.500" />
            <Text color="gray.500">Loading payroll workspace...</Text>
          </VStack>
        ) : null}

        {!loading && sectionKey === "runs" ? (
          <RunsSection
            canManagePayroll={canManagePayroll}
            runForm={runForm}
            setRunForm={setRunForm}
            handleCreateRun={handleCreateRun}
            payrollRuns={payrollRuns}
            handleRunAction={handleRunAction}
          />
        ) : null}

        {!loading && sectionKey === "ctc" ? (
          <CtcSection
            canManagePayroll={canManagePayroll}
            structureForm={structureForm}
            setStructureForm={setStructureForm}
            handleCreateStructure={handleCreateStructure}
            compensationForm={compensationForm}
            setCompensationForm={setCompensationForm}
            employees={employees}
            salaryStructures={salaryStructures}
            handleAssignCompensation={handleAssignCompensation}
            compensations={compensations}
            meta={meta}
            formatPayrollCurrency={formatPayrollCurrency}
          />
        ) : null}

        {!loading && sectionKey === "compliance" ? (
          <ComplianceSection
            canManagePayroll={canManagePayroll}
            canFinance={canFinance}
            statutoryForm={statutoryForm}
            setStatutoryForm={setStatutoryForm}
            handleSaveStatutory={handleSaveStatutory}
            complianceDashboard={complianceDashboard}
            meta={meta}
            formatPayrollCurrency={formatPayrollCurrency}
            complianceEdits={complianceEdits}
            setComplianceEdits={setComplianceEdits}
            complianceModalRecord={complianceModalRecord}
            setComplianceModalRecord={setComplianceModalRecord}
            isComplianceModalOpen={isComplianceModalOpen}
            openComplianceModal={openComplianceModal}
            closeComplianceModal={closeComplianceModal}
            handleComplianceUpdate={handleComplianceUpdate}
          />
        ) : null}

        {!loading && sectionKey === "benefits" ? (
          <BenefitsSection
            canManagePayroll={canManagePayroll}
            benefitForm={benefitForm}
            setBenefitForm={setBenefitForm}
            adjustmentForm={adjustmentForm}
            setAdjustmentForm={setAdjustmentForm}
            employees={employees}
            handleCreateBenefit={handleCreateBenefit}
            handleCreateAdjustment={handleCreateAdjustment}
            benefits={benefits}
            adjustments={adjustments}
            meta={meta}
            formatPayrollCurrency={formatPayrollCurrency}
          />
        ) : null}

        {!loading && sectionKey === "fnf" ? (
          <FnfSection
            canManagePayroll={canManagePayroll}
            canFinance={canFinance}
            fnfForm={fnfForm}
            setFnfForm={setFnfForm}
            employees={employees}
            handleCreateFnf={handleCreateFnf}
            fnfRecords={fnfRecords}
            fnfEdits={fnfEdits}
            setFnfEdits={setFnfEdits}
            handleUpdateFnf={handleUpdateFnf}
            meta={meta}
            formatPayrollCurrency={formatPayrollCurrency}
          />
        ) : null}

        {!loading && sectionKey === "form16" ? (
          <Form16Section
            canFinance={canFinance}
            form16Form={form16Form}
            setForm16Form={setForm16Form}
            employees={employees}
            handleGenerateForm16={handleGenerateForm16}
            form16Records={form16Records}
            form16Edits={form16Edits}
            setForm16Edits={setForm16Edits}
            handleUpdateForm16={handleUpdateForm16}
            meta={meta}
            formatPayrollCurrency={formatPayrollCurrency}
          />
        ) : null}

        {!loading && sectionKey === "bank" ? (
          <BankSection
            bankTransfers={bankTransfers}
            bankEdits={bankEdits}
            setBankEdits={setBankEdits}
            canFinance={canFinance}
            handleBankPreview={handleBankPreview}
            handleBankTransferUpdate={handleBankTransferUpdate}
            bankPreview={bankPreview}
            meta={meta}
            formatPayrollCurrency={formatPayrollCurrency}
          />
        ) : null}

        {!loading && sectionKey === "payslips" ? (
          <PayslipsSection
            payslips={payslips}
            user={user}
            meta={meta}
            formatPayrollCurrency={formatPayrollCurrency}
          />
        ) : null}
      </Stack>
    </Box>
  );
};

export default PayrollPage;
